"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Score } from "@/lib/chess/uci";

export type CoachRequest = {
  mode: "move" | "position" | "question";
  fen: string;
  moveSan?: string;
  engine?: { before?: { score: Score; pv?: string[] }; after?: { score: Score; pv?: string[] } };
  rating?: number;
  openingName?: string;
  question?: string;
  history?: { role: "user" | "assistant"; content: string }[];
};

/** Streams text from /api/coach. A new request cancels the previous one. */
export function useCoach() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ctrl = useRef<AbortController | null>(null);
  useEffect(() => () => ctrl.current?.abort(), []);

  const ask = useCallback(async (req: CoachRequest): Promise<string> => {
    ctrl.current?.abort();
    const ac = new AbortController();
    ctrl.current = ac;
    setText("");
    setError(null);
    setSource(null);
    setLoading(true);
    let full = "";
    try {
      const res = await fetch("/api/coach", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(req),
        signal: ac.signal,
      });
      if (ac.signal.aborted || ctrl.current !== ac) return "";
      setSource(res.headers.get("x-coach-source"));
      if (!res.body) throw new Error("No response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (ac.signal.aborted || ctrl.current !== ac) return "";
        if (done) break;
        full += dec.decode(value, { stream: true });
        setText(full);
      }
      // Show the error, but never hand it back as an answer for the conversation history.
      if (!res.ok) {
        setText("");
        setError(full || `Coach error ${res.status}`);
        return "";
      }
      return full;
    } catch (e) {
      if (ac.signal.aborted || ctrl.current !== ac) return "";
      if ((e as Error).name !== "AbortError") setError("The coach is unavailable right now.");
      return "";
    } finally {
      if (ctrl.current === ac) setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    ctrl.current?.abort();
    setText("");
    setError(null);
    setSource(null);
    setLoading(false);
  }, []);

  return { text, loading, source, error, ask, reset };
}
